import React, { useState } from 'react';
import { Spinner, ErrorBox, EmptyState } from './UI';
import { useMatches, useMatchdays } from '../hooks/useApi';

function teamName(team) {
  if (!team) return '?';
  if (typeof team === 'string') return team;
  return team.officialName || team.mediaName || team.shortName || team.acronymName || '?';
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('it-IT', {
      weekday: 'short', day: '2-digit', month: 'short', year: 'numeric'
    });
  } catch { return dateStr; }
}

function formatTime(dateStr) {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

function statusClass(status) {
  if (!status) return '';
  const s = status.toUpperCase();
  if (s === 'LIVE' || s === 'INPROGRESS') return 'status-live';
  if (s === 'FINISHED' || s === 'PLAYED')  return 'status-finished';
  if (s === 'UPCOMING' || s === 'FIXTURE') return 'status-upcoming';
  return 'status-other';
}

function statusText(status) {
  if (!status) return '';
  const s = status.toUpperCase();
  if (s === 'LIVE' || s === 'INPROGRESS') return '🔴 LIVE';
  if (s === 'FINISHED' || s === 'PLAYED')  return 'Terminata';
  if (s === 'UPCOMING' || s === 'FIXTURE') return 'In programma';
  return status;
}

export default function MatchesList({ season, onSelectMatch }) {
  const { matchdays } = useMatchdays(season?.seasonId);
  const [selectedMatchday, setSelectedMatchday] = useState('');
  const { matches, loading, error } = useMatches(
    season?.seasonId,
    selectedMatchday || undefined
  );

  const flatMatches = (() => {
    if (!Array.isArray(matches)) return [];
    if (matches.length > 0 && matches[0]?.matches) {
      return matches.flatMap(set => set.matches || []);
    }
    return matches;
  })();

  return (
    <section className="selector-section">
      <div className="section-header-row">
        <h2 className="section-title">
          Partite — <span className="section-sub">{season?.seasonName}</span>
        </h2>

        {matchdays.length > 0 && (
          <select className="select-input" value={selectedMatchday}
            onChange={e => setSelectedMatchday(e.target.value)}>
            <option value="">Tutte le giornate</option>
            {matchdays.map((md, idx) => {
              const id  = md.matchDayId || md.id || md.matchday || String(idx);
              const num = md.matchday || md.matchDayNumber || md.number || (idx + 1);
              return (
                <option key={`md-${id}-${idx}`} value={id}>
                  Giornata {num}{md.status ? ` — ${md.status}` : ''}
                </option>
              );
            })}
          </select>
        )}
      </div>

      {loading && <Spinner label="Caricamento partite…" />}
      {error   && <ErrorBox message={`Impossibile caricare le partite: ${error}`} />}

      {!loading && !error && flatMatches.length === 0 && (
        <EmptyState icon="📋" title="Nessuna partita disponibile"
          subtitle="Prova a selezionare un'altra giornata" />
      )}

      {!loading && flatMatches.length > 0 && (
        <div className="matches-list">
          {flatMatches.map((match, idx) => {
            const matchId  = match?.matchId || match?.optaId || match?.id;
            const homeTeam = match?.home || match?.homeTeam;
            const awayTeam = match?.away || match?.awayTeam;
            const homeName = teamName(homeTeam);
            const awayName = teamName(awayTeam);

            const homeScore = match?.homeScorePush ?? match?.providerHomeScore ?? match?.homeScore ?? null;
            const awayScore = match?.awayScorePush ?? match?.providerAwayScore ?? match?.awayScore ?? null;
            const hasScore  = homeScore !== null && awayScore !== null;

            const date     = match?.matchDateLocal || match?.kickOffDate || match?.date || match?.kickoff;
            const matchday = match?.matchday || match?.matchDayNumber || match?.matchDayLabel;
            const status   = match?.status;

            return (
              <button
                key={matchId ? `match-${matchId}` : `match-idx-${idx}`}
                className="match-row-new"
                onClick={() => onSelectMatch && onSelectMatch(match)}
              >
                {/* Riga superiore: giornata + data + status */}
                <div className="match-row-meta">
                  {matchday != null && <span className="match-day">Giornata {matchday}</span>}
                  <span className="match-date-full">{formatDate(date)}{date ? ` — ${formatTime(date)}` : ''}</span>
                  {status && (
                    <span className={`match-status ${statusClass(status)}`}>
                      {statusText(status)}
                    </span>
                  )}
                </div>

                {/* Riga centrale: punteggio + nomi squadre */}
                <div className="match-row-body">
                  <div className="match-team-block match-team-home">
                    <span className="match-team-full">{homeName}</span>
                  </div>

                  <div className="match-score-block">
                    {hasScore ? (
                      <>
                        <span className="match-score-num">{homeScore}</span>
                        <span className="match-score-dash">–</span>
                        <span className="match-score-num">{awayScore}</span>
                      </>
                    ) : (
                      <span className="match-score-vs">vs</span>
                    )}
                  </div>

                  <div className="match-team-block match-team-away">
                    <span className="match-team-full">{awayName}</span>
                  </div>
                </div>

                <span className="match-row-arrow">›</span>
              </button>
            );
          })}
        </div>
      )}

      {!loading && flatMatches.length > 0 && (
        <p className="count-label">
          {flatMatches.length} partit{flatMatches.length === 1 ? 'a' : 'e'}
        </p>
      )}
    </section>
  );
}
